import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { Alert } from '@material-ui/lab';
import { setBulkEmailStatus, startSettingBulkPreview } from 'actions/bulkEmail';
import { BulkEmailSequencesPreview } from 'features/bulk-email/sequences/components/BulkEmalSequencesPreview';
import { SEQUENCE_STATUS_IDS } from 'features/bulk-email/sequences/constants';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import { BulkMenuItems } from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { dateFormatter } from 'UI/utils';

import { PreviewMetaRowsSections } from './components/PreviewMetaRowsSections/PreviewMetaRowsSections';
import { usePreviewBulkEmailStyles } from './styles';
import { getMailMeta } from './utils';

const { mailMeta: MAIL_META_STRINGS, sequences: sequencesCopies } = strings.bulkEmails.preview;

export const buildDateToRender = (selectedMenuItem, createdAt, sendDate) => {
  const date = selectedMenuItem === BulkMenuItems.Drafts ? createdAt : sendDate;
  return dateFormatter(date, ({ MonthDayYearTime }) => MonthDayYearTime);
};

const PreviewBulkEmail = ({
  bulkInPreviewData,
  selectedMenuItem,
  shouldReloadEmailList = false
}) => {
  const dispatch = useDispatch();
  const classes = usePreviewBulkEmailStyles();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const areBulkSequencesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailSequences);

  const {
    id: bulkId = null,
    emailBody: { html: emailBody = '', signature = '', subject = '' } = {}
  } = bulkInPreviewData || {};
  const sequences = bulkInPreviewData?.sequences;

  const { mainMailMeta, secondaryMailMeta, sequencesMeta } = getMailMeta({
    bulkInPreviewData,
    selectedMenuItem,
    isSequencesActive: areBulkSequencesEnabled
  });

  const shouldLoadSequencesPreview = !!(areBulkSequencesEnabled && sequencesMeta);
  const shouldShowCanceledAlert =
    sequences?.stoppedById !== bulkInPreviewData?.userInfo?.id &&
    sequences?.statusId === SEQUENCE_STATUS_IDS.canceled;

  const finalEmailBody = useMemo(() => {
    if (!emailBody) {
      return MAIL_META_STRINGS.emailBody.defaultValue;
    }

    // TODO: can this feature flag and validation be removed already?
    return signature ? `${emailBody}${signature}` : emailBody;
  }, [emailBody, signature]);

  useEffect(() => {
    if (shouldReloadEmailList && bulkId) {
      dispatch(startSettingBulkPreview(bulkId));
      dispatch(setBulkEmailStatus({ reloadEmailList: false }));
    }
  }, [bulkId, dispatch, shouldReloadEmailList]);

  const CommonPreviewMetaRowsSectionsProps = useMemo(
    () => ({
      emailBody: finalEmailBody,
      firstSectionData: mainMailMeta,
      secondarySectionData: secondaryMailMeta,
      subject: subject || MAIL_META_STRINGS.subject.defaultValue
    }),
    [finalEmailBody, mainMailMeta, secondaryMailMeta, subject]
  );

  return !bulkId ? null : (
    <Grid container className={classes.mainContainer}>
      {shouldLoadSequencesPreview ? (
        <>
          <When condition={shouldShowCanceledAlert}>
            <Grid container item className={classes.cancelAlertContainer}>
              <Alert severity="warning" className={classes.fullWidth}>
                {strings.formatString(sequencesCopies.canceledAlert, {
                  noun: sequences.stoppedByName,
                  date: dateFormatter(sequences.stoppedAt, formats => formats.SimpleDate)
                })}
              </Alert>
            </Grid>
          </When>
          <BulkEmailSequencesPreview
            initialBulkConfig={{
              id: bulkInPreviewData.id,
              scope: bulkInPreviewData.emailConfig.bulkType,
              searchProject: bulkInPreviewData.searchProject,
              candidates: bulkInPreviewData.emailConfig.marketing.marketingCandidates,
              jobOrders: bulkInPreviewData.emailConfig.recruiting.recruitingJobOrders,
              userEmail: bulkInPreviewData.userInfo.email
            }}
            initialBulkMetaRows={
              <PreviewMetaRowsSections
                status={{
                  id: bulkInPreviewData.sequences.initialBulk.statusId,
                  title: bulkInPreviewData.sequences.initialBulk.statusTitle
                }}
                {...CommonPreviewMetaRowsSectionsProps}
              />
            }
            sequencesMeta={sequencesMeta}
          />
        </>
      ) : (
        <PreviewMetaRowsSections
          {...CommonPreviewMetaRowsSectionsProps}
          isEmailBodySectionAlwaysVisible
        />
      )}
    </Grid>
  );
};

export default PreviewBulkEmail;
